import { accountStatusesKey } from '../keys'
import { useQueryClient } from '@tanstack/react-query'
import { type AccountStatusData, type AccountStatusListSuccess, Logger, sortAccountStatuses } from '@terros/common'

const logger = new Logger('useAccountStatusCache')
// logger.level = 'verbose'

type Cached = AccountStatusListSuccess

type AccountStatusCache = {
  clearList: VoidFunction
  replaceList: (response: Cached) => void
  addItem: (status: AccountStatusData) => void
  replaceItem: (status: AccountStatusData) => void
  removeItem: (statusId: string) => void
}

export function useAccountStatusCache(): AccountStatusCache {
  const queryClient = useQueryClient()
  return {
    clearList: () => {
      logger.verbose('invalidateQueries accountStatusesKey')
      queryClient.invalidateQueries({ queryKey: accountStatusesKey() })
    },
    replaceList: (response: Cached) =>
      queryClient.setQueryData<Cached>(accountStatusesKey(), {
        ...response,
        statuses: sortAccountStatuses(response.statuses),
      }),
    addItem: (status: AccountStatusData) => {
      logger.verbose('replaceItem', status)
      queryClient.setQueriesData<Cached>({ queryKey: accountStatusesKey() }, (existing) => {
        if (!existing) return
        return {
          ...existing,
          statuses: sortAccountStatuses([...existing.statuses, status]),
        }
      })
    },
    replaceItem: (status: AccountStatusData) => {
      logger.verbose('replaceItem', status)
      queryClient.setQueriesData<Cached>({ queryKey: accountStatusesKey() }, (existing) => {
        if (!existing) return
        return {
          ...existing,
          statuses: sortAccountStatuses(
            existing.statuses.map((item) => (item.statusId === status.statusId ? status : item))
          ),
        }
      })
    },
    removeItem: (statusId: string) => {
      logger.verbose('removeItem', statusId)
      queryClient.setQueriesData<Cached>({ queryKey: accountStatusesKey() }, (existing) => {
        if (!existing) return
        return {
          ...existing,
          statuses: sortAccountStatuses(existing.statuses.filter((item) => item.statusId !== statusId)),
        }
      })
    },
  }
}
